import { UseQueryResult } from "@tanstack/react-query"
import { FullSpinner } from "../common/Spinner"
import { ApiError } from "../../services/user"
import { useI18nContext } from "../../i18n/i18n-react"
import Retry from "../../assets/retry.svg?react"
import NotFound from "./NotFound"
import Clipboard from "react-clipboard.js"
import { TimeoutError } from "ky"

export const ErrorRetry = ({ retry, err }: { retry: () => void; err?: unknown }) => {
  const { LL } = useI18nContext()
  return (
    <div className="flex flex-col h-full w-full items-center justify-around pb-10">
      <div className="grow flex flex-col justify-center items-center space-y-5">
        <Retry />

        <div className="text-sm">{!!err && err instanceof TimeoutError ? LL.timeoutError() : LL.pageError()}</div>
      </div>

      <button className="w-[calc(100%-5rem)] bg-[#444444] h-11 rounded text-white" onClick={retry}>
        {LL.retry()}
      </button>

      {!!err && (
        <Clipboard className="text-[#BCBEC3] border-b text-sm py-2 flex-center" data-clipboard-text={JSON.stringify(err, null, 2)}>
          {LL.copyErrorDetail()}
        </Clipboard>
      )}
    </div>
  )
}

export type Query<T> = Pick<UseQueryResult<T, unknown>, "status" | "error"> & {
  refetch: () => void
}

export function LoaderLayout<T>({
  queries,
  children,
}: {
  queries: Query<T>[] | Query<T>
} & {
  children?: React.ReactNode
}) {
  const finalQueries = Array.isArray(queries) ? queries : [queries]

  const status = finalQueries.some((query) => query.status === "error") ? "error" : finalQueries.some((query) => query.status === "loading") ? "loading" : "success"
  const errors = finalQueries.map((query) => query.error).filter(Boolean)
  const error = errors?.[0]
  const refetch = () => {
    finalQueries.filter((query) => query.status === "error").forEach((query) => query.refetch())
  }


  switch (status) {
    case "loading":
      return <FullSpinner className="grow" />
    case "error":
      if (error instanceof ApiError && error.code === 404) {
        return <NotFound />
      }
      return <ErrorRetry retry={refetch} err={error} />
    case "success":
      return children
  }
}
